import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { PostCard } from "@/components/PostCard";
import SEO, { SITE_ORIGIN, SITE_NAME } from "@/components/SEO";
import Ticker from "@/components/Ticker";

export default function ListPage({ type, types, title, subtitle, hideTopics, cardLabel }) {
  const [posts, setPosts] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const topic = params.get("topic") || "";
  const location = useLocation();

  useEffect(() => {
    api.get("/topics").then((r) => setTopics(r.data.topics || []));
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (type) q.type = type;
    if (topic) q.topic = topic;
    api.get("/posts", { params: q })
      .then((r) => {
        const data = r.data;
        const filtered = types && types.length ? data.filter((p) => types.includes(p.type)) : data;
        setPosts(filtered);
      })
      .finally(() => setLoading(false));
  }, [type, types, topic]);

  const setTopic = (t) => {
    if (t) setParams({ topic: t });
    else setParams({});
  };

  const listStructuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: title,
    url: `${SITE_ORIGIN}${location.pathname}`,
    isPartOf: { "@type": "WebSite", name: SITE_NAME, url: SITE_ORIGIN },
    description: subtitle || `Public ${title} on ${SITE_NAME}.`,
    hasPart: posts.slice(0, 25).map((p) => ({
      "@type": type === "poll" ? "Question" : "Article",
      name: (p.title || "").replace(/\s*\n+\s*/g, " "),
      url: `${SITE_ORIGIN}/post/${p.id}`,
    })),
  };

  return (
    <Layout>
      <SEO
        path={location.pathname}
        title={title}
        description={subtitle || `Read the latest ${title} on ${SITE_NAME} — moderated citizen contributions on politics and media.`}
        structuredData={listStructuredData}
      />
      <Ticker />
      <section>
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6 border-b border-black">
            <h1 className="serif text-5xl sm:text-6xl tracking-tight">{title}</h1>
            {subtitle && <p className="text-neutral-700 mt-3 max-w-2xl">{subtitle}</p>}
          </div>
        </div>
      </section>

      {/* Topic filter */}
      {false && !hideTopics && (
      <section className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6 border-b border-neutral-200">
        <div className="flex flex-wrap items-center gap-2">
          <span className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 mr-2">Topic:</span>
          <button
            data-testid="topic-filter-all"
            onClick={() => setTopic("")}
            className={`mono text-xs uppercase tracking-[0.2em] px-3 py-1 border transition-colors ${
              topic === "" ? "bg-black text-white border-black" : "border-neutral-300 hover:border-black"
            }`}
          >
            All
          </button>
          {topics.map((t) => (
            <button
              key={t}
              data-testid={`topic-filter-${t}`}
              onClick={() => setTopic(t)}
              className={`mono text-xs uppercase tracking-[0.2em] px-3 py-1 border transition-colors ${
                topic === t ? "bg-black text-white border-black" : "border-neutral-300 hover:border-black"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </section>
      )}

      <section className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading && <p className="mono text-sm text-neutral-500">Loading&hellip;</p>}
        {!loading && posts.length === 0 && (
          <div className="border border-dashed border-neutral-400 p-12 text-center">
            <h4 className="serif text-2xl">Nothing here yet.</h4>
            <p className="text-neutral-600 mt-2">Check back soon, or contribute your own.</p>
          </div>
        )}
        {!loading && posts.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10">
            {posts.map((p) => (
              <PostCard key={p.id} post={p} labelOverride={cardLabel} />
            ))}
          </div>
        )}
      </section>
    </Layout>
  );
}
